// Conditions of a transaction mapping ("Regel") — ticket 2506.
//
// Every mapping carries its own condition: the counterpart IBAN, a text qualifier the
// transaction's purpose must contain, or both — then both must hold. One of the two is
// required. The finance service is the authority on matching: it applies mappings on bank
// import and to existing transactions, and answers which mappings fit a transaction. The
// matcher here mirrors it for the hints in the modal and the interim lookup, so its
// semantics must stay identical to the backend's — change both or neither.

// On locally and on staging, off in production (app.hellohousing.de) — the same hostname
// check Setting.js uses to pick the API. Production waits until the finance service stores
// and evaluates `textQualifier`: a backend that drops it would save a mapping with a text
// qualifier as IBAN-only, broader than what the user approved.
export const TEXT_QUALIFIER_RULES_ENABLED = !window.location.hostname.includes('app');

// Purposes reach us re-wrapped from the bank's fixed-width fields (MT940 sub-fields are 27
// characters), so a space can land mid-word: "Hausgeld" arrives as "Hausg eld". Comparing
// with all whitespace removed lets the qualifier survive that; lower-casing makes it
// case-insensitive.
export const normalizeRuleText = (text) => (text || '').toString().toLocaleLowerCase('de-DE').replace(/\s+/g, '');

const normalizeIban = (iban) => (iban || '').toString().toUpperCase().replace(/\s+/g, '');

const hasTextQualifier = (mapping) => normalizeRuleText(mapping?.textQualifier) !== '';

export const textQualifierFound = (textQualifier, transaction) => {
  const needle = normalizeRuleText(textQualifier);
  return needle !== '' && normalizeRuleText(transaction?.purpose).includes(needle);
};

// Mappings saved before 2506 have no textQualifier and match on the IBAN alone.
export const mappingMatchesTransaction = (mapping, transaction) => {
  const hasIban = !!mapping?.counterpartIban;
  if(!hasIban && !hasTextQualifier(mapping)){
    return false;
  }
  if(hasIban && normalizeIban(mapping.counterpartIban) !== normalizeIban(transaction?.counterpartIban)){
    return false;
  }
  return !hasTextQualifier(mapping) || textQualifierFound(mapping.textQualifier, transaction);
};

// How narrowly a mapping picks its payments: IBAN and text, then IBAN, then text. An IBAN
// identifies the payer exactly; a text match can come from any sender.
export const getMappingSpecificity = (mapping) => (mapping?.counterpartIban ? 2 : 0) + (hasTextQualifier(mapping) ? 1 : 0);

// The mappings that fit this transaction, most specific first; ties keep the backend's order.
export const selectMatchingMappings = (mappings, transaction) => (mappings || [])
  .filter((mapping) => mappingMatchesTransaction(mapping, transaction))
  .sort((a, b) => getMappingSpecificity(b) - getMappingSpecificity(a));

// null when a condition can be saved for this transaction, otherwise why it can't.
export const getMappingConditionError = (condition, transaction) => {
  if(!condition?.counterpartIban && !hasTextQualifier(condition)){
    return 'Bitte IBAN oder Verwendungszweck angeben.';
  }
  // A mapping edited from this transaction has to keep fitting it: otherwise it would drop out
  // of the modal on the next reload, and a qualifier that isn't in the purpose is almost
  // always a typo.
  if(hasTextQualifier(condition) && !textQualifierFound(condition.textQualifier, transaction)){
    return 'Nicht im Verwendungszweck dieser Zahlung enthalten.';
  }
  return null;
};
